//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { CatalogIdentifierDisplay } from '@campaigns/components/shared/product-details-modal/catalog/CatalogIdentifierDisplay';
import { FormField } from '@campaigns/components/shared/product-details-modal/form/FormField';
import { TagsInput } from '@campaigns/components/shared/tags-input/TagsInput';
import { ProductDetailsFieldLabels } from '@campaigns/constants/ProductDetailsModalFieldLabels';
import { useProductModal } from '@campaigns/contexts/ProductModalContext';
import { useProductSearch } from '@campaigns/hooks/useProductSearch';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { validateProductIdentifier } from '@campaigns/utils/sampling/productIdentifierUtils';
import { BodyText } from '@components/atoms/text/Text';
import { DEFAULT_EMPTY_VALUE } from '@constants/DefaultEmptyValues';
import { isDefined, isTruthy } from '@utils/filterPredicates';

interface ProductIdentifierFieldsProps {
  isViewOnly: boolean;
}

export const ProductIdentifierFields = ({
  isViewOnly,
}: ProductIdentifierFieldsProps) => {
  const { samplingCampaignRequest } = useSamplingCampaign();
  const { control, setValue, watch } =
    useFormContext<EditableBVSamplingProduct>();
  const { productModalState } = useProductModal();
  const variations = useWatch({ control, name: 'variations' });
  const hasVariations = variations.length > 1;

  const fulfillmentType =
    samplingCampaignRequest?.fulfillmentOptions?.fulfillmentType;
  const whoShipsProduct =
    samplingCampaignRequest?.fulfillmentOptions?.whoShipsProduct;
  const isBVShipped =
    fulfillmentType === 'shipped' && whoShipsProduct === 'bazaarvoice';

  // Watch the GTIN value for the first variation
  const gtinValue = useWatch({
    control,
    name: 'variations.0.logisticsRef',
  });

  // Compute validation state based on the watched value
  const isGtinValid = gtinValue
    ? validateProductIdentifier(gtinValue).isValid
    : false;

  // GTIN values handling
  const gtinValues =
    watch('destinationGTIN')
      ?.split(',')
      .map((s) => s.trim())
      .filter(Boolean) ?? [];

  const handleDestinationGtinChange = (tags: string[]) => {
    setValue('destinationGTIN', tags.join(', '), {
      shouldValidate: true,
    });
  };

  // Catalog product data
  const { data: catalogProduct } = useProductSearch({
    productIds: isDefined(productModalState.product?.targetCatalogProductId)
      ? [productModalState.product.targetCatalogProductId]
      : undefined,
    enabled: isDefined(productModalState.product?.targetCatalogProductId),
  });

  const upcs = catalogProduct?.products[0]?.upcs ?? [];
  const eans = catalogProduct?.products[0]?.eans ?? [];

  const hasUpcs = upcs.length > 0;
  const hasEans = eans.length > 0;
  const totalIdentifiers = upcs.length + eans.length;
  const shouldShowIdentifiers = !isViewOnly && totalIdentifiers > 1;

  return (
    <Container>
      {/* Barcode Number (GTIN) Field */}
      {!hasVariations && (
        <FormField
          helpText={
            !isViewOnly && !isBVShipped && !isGtinValid && isTruthy(gtinValue)
              ? 'We highly recommend providing a valid GTIN, UPC, EAN or PZN, having a valid value can improve product identification and syndication matching.'
              : ''
          }
          inputType='text'
          isViewOnly={isViewOnly}
          label={ProductDetailsFieldLabels.logisticsRef}
          labelHelpText={
            isBVShipped
              ? 'Enter the 7-8 or 12 to 14-digit unique product code, this is the GTIN, UPC, EAN or PZN associated to the product. This ensures our fulfillment partners have the correct product information.'
              : 'Enter the 7-8 or 12 to 14-digit unique product code, this is the GTIN, UPC, EAN or PZN associated to the product. This ensures accurate product identification for syndication.'
          }
          name='variations.0.logisticsRef'
          placeholder='1309945678912'
          rules={{
            required: {
              value: true,
              message: 'Please enter a GTIN, UPC, EAN or PZN',
            },
            validate: (value: string) => {
              // For BV shipped products, enforce strict validation
              // For non-BV shipped products, show warning but allow submission
              if (!isBVShipped) {
                return true;
              }

              // For BV shipped products, return the error message if validation failed
              return validateProductIdentifier(value).reason ?? true;
            },
          }}
          visibleToParticipants={false}
          width='half'
        />
      )}

      {/* GTINs/UPCs/EANs Field */}
      {isViewOnly ? (
        <TagsInputContainer>
          <BodyText>GTINs/UPCs/EANs for Syndication (optional)</BodyText>
          <BodyText>
            {gtinValues.length > 0
              ? gtinValues.join(', ')
              : DEFAULT_EMPTY_VALUE}
          </BodyText>
        </TagsInputContainer>
      ) : (
        <TagsInput
          label={ProductDetailsFieldLabels.destinationGTIN}
          onTagsChange={handleDestinationGtinChange}
          placeholder='1309945678912, 0306245678912, 0506975678912'
          selectedItemsLabel='Added to campaign request'
          values={gtinValues}
        />
      )}

      {shouldShowIdentifiers && (
        <IdentifiersContainer>
          <BodyText>In Catalog</BodyText>
          {hasUpcs && (
            <CatalogIdentifierDisplay identifiers={upcs} title='UPCs' />
          )}
          {hasEans && (
            <CatalogIdentifierDisplay identifiers={eans} title='EANs' />
          )}
        </IdentifiersContainer>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[3]};
`;

const TagsInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const IdentifiersContainer = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[2]};
    margin-top: ${theme.spacings[2]};
`,
);
